import React from 'react';

const FeatureFlags = ({ flags, onUpdate }) => {
  if (!flags) {
    return (
      <div className="card">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const flagsData = flags.flags || {};
  const metadata = {
    version: flags.version || 'N/A',
    updated_at: flags.updated_at || 'N/A',
    updated_by: flags.updated_by || 'N/A',
  };

  const getFlagDescription = (flagName) => {
    const descriptions = {
      refi_accounting_enabled: 'Master switch for all refi accounting automation',
      refi_old_loan_status_update: 'Update old loan status to "Closed - Refinanced" and set closed date',
      refi_old_loan_notes: 'Add refinance note to old loan',
      refi_initial_credit: 'Apply initial credit to old loan (RefiPayoff amount)',
      refi_rol_reversal: 'Reverse $110 ROL fee on old loan',
      refi_final_adjustments: 'Apply final adjustment (credit/charge/advancement) based on calculation',
      always_generate_csvs: 'Always generate CSVs regardless of toggle state (for QC)',
    };
    return descriptions[flagName] || 'No description available';
  };

  const getFlagColor = (enabled) => {
    return enabled ? '#4caf50' : '#dc3545';
  };

  return (
    <div className="card">
      <h2>Feature Flags</h2>
      <p style={styles.description}>
        Current configuration of refi accounting feature flags. These flags control which steps are executed during refi processing.
      </p>

      {/* Metadata */}
      <div style={styles.metadata}>
        <div style={styles.metadataItem}>
          <strong>Version:</strong> {metadata.version}
        </div>
        <div style={styles.metadataItem}>
          <strong>Last Updated:</strong> {metadata.updated_at}
        </div>
        <div style={styles.metadataItem}>
          <strong>Updated By:</strong> {metadata.updated_by}
        </div>
      </div>

      {/* Flags Table */}
      <div style={styles.tableContainer}>
        <table className="table">
          <thead>
            <tr>
              <th>Flag Name</th>
              <th>Description</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(flagsData).map((flagName) => (
              <tr key={flagName}>
                <td>
                  <code style={styles.flagName}>{flagName}</code>
                </td>
                <td style={styles.description}>{getFlagDescription(flagName)}</td>
                <td>
                  <div style={styles.statusContainer}>
                    <div
                      style={{
                        ...styles.statusIndicator,
                        backgroundColor: getFlagColor(flagsData[flagName]),
                      }}
                    ></div>
                    <span style={styles.statusText}>
                      {flagsData[flagName] ? 'ENABLED' : 'DISABLED'}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Info Box */}
      <div style={styles.infoBox}>
        <h4 style={styles.infoTitle}>ℹ️ Important Notes:</h4>
        <ul style={styles.infoList}>
          <li>
            <strong>always_generate_csvs</strong> is always enabled and cannot be disabled. CSVs are generated for all steps, even if their flags are disabled.
          </li>
          <li>
            <strong>refi_accounting_enabled</strong> is the master switch. If disabled, all refi accounting is skipped (but CSVs are still generated).
          </li>
          <li>
            Feature flags can be updated using the CLI tool: <code>python scripts/manage_feature_flags.py</code>
          </li>
          <li>
            Changes to feature flags take effect immediately for new refi processing events.
          </li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div style={styles.actions}>
        <button className="button button-primary" onClick={onUpdate}>
          🔄 Refresh Flags
        </button>
        <button
          className="button button-secondary"
          onClick={() => alert('CLI tool usage:\npython scripts/manage_feature_flags.py enable <flag_name>\npython scripts/manage_feature_flags.py disable <flag_name>')}
        >
          📖 CLI Instructions
        </button>
      </div>
    </div>
  );
};

const styles = {
  description: {
    color: '#666',
    marginBottom: '20px',
  },
  metadata: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
  },
  metadataItem: {
    fontSize: '14px',
  },
  tableContainer: {
    overflowX: 'auto',
    marginBottom: '20px',
  },
  flagName: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#1976d2',
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  statusIndicator: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
  },
  statusText: {
    fontSize: '12px',
    fontWeight: '600',
  },
  infoBox: {
    padding: '20px',
    backgroundColor: '#e3f2fd',
    borderLeft: '4px solid #1976d2',
    borderRadius: '4px',
    marginBottom: '20px',
  },
  infoTitle: {
    fontSize: '16px',
    marginBottom: '10px',
    color: '#1976d2',
  },
  infoList: {
    paddingLeft: '20px',
    margin: 0,
    fontSize: '14px',
    lineHeight: '1.8',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
};

export default FeatureFlags;

